import React from "react";
import { StopsFilter } from "./routeSearchHelpers";

interface MobileStopsFilterProps {
  isOpen: boolean;
  onClose: () => void;
  resultCount: number;
  readOnly?: boolean;
  stopsFilter: StopsFilter;
  onStopsFilterChange: (filter: StopsFilter) => void;
  hideConnectedFilter?: boolean;
  hideThreeStops?: boolean;
}

const LABELS: Record<string, string> = {
  none: "Non-stop",
  one: "1 Stop",
  two: "2 Stops",
  three: "3 Stops",
  "one-or-fewer": "Connected",
};

// Bottom-sheet stops filter shown on mobile (the desktop sidebar lives in
// SearchResults).
const MobileStopsFilter: React.FC<MobileStopsFilterProps> = ({
  isOpen,
  onClose,
  resultCount,
  readOnly = false,
  stopsFilter,
  onStopsFilterChange,
  hideConnectedFilter = false,
  hideThreeStops = false,
}) => {
  if (!isOpen) return null;

  const allValues =
    readOnly || hideConnectedFilter
      ? (["none", "one", "two", "three"] as const)
      : (["none", "one", "two", "three", "one-or-fewer"] as const);
  const values = hideThreeStops
    ? allValues.filter((v) => v !== "three")
    : allValues;

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[#1F2937] text-base font-bold">Filters</p>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <p className="text-[#9CA3AF] text-xs mb-4">Showing {resultCount} results</p>
        <p className="text-[#1F2937] text-sm font-semibold mb-3">Stops</p>
        <div className="flex flex-col gap-3">
          {values.map((val) => (
            <label
              key={val}
              className="flex items-center cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="stops-mobile-route"
                  checked={stopsFilter === val}
                  onChange={() => {
                    onStopsFilterChange(val);
                    onClose();
                  }}
                  className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                />
                <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                  {LABELS[val]}
                </span>
              </div>
            </label>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            onStopsFilterChange("none");
            onClose();
          }}
          className="w-full mt-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
};

export default MobileStopsFilter;
